import { HStack, PageContainer, Text, VStack } from '@/components/Atoms'
import { CrtImportHistoryButtons } from '@/components/CrtImportHistoryButtons'
import { CrtImportPeriodNotFound } from '@/components/CrtImportPeriodNotFound'
import { DetailsHeader } from '@/components/DetailsHeader'
import { ExportCsvMenu, ExportCsvMenuItem } from '@/components/ExportCsv/ExportCsvMenu'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { Note } from '@/components/Note'
import { PendingChanges } from '@/components/PendingChanges'
import { Spinner } from '@/components/Spinner'
import {
  currentBpcActualsQueryOptions,
  bpcActualsDetailsExportQueryOptions,
  bpcActualsDetailsRawExportQueryOptions,
  bpcActualsDetailsSearchSchema,
  useBpcActualsById,
  useUpdateBpcActuals,
} from '@/hooks/queries/crt/useBpcActuals'
import { useLastUpdatedBpcActualsLog } from '@/hooks/queries/useAuditTrails'
import { useTablePagination } from '@/hooks/useTablePagination'
import { formatLastUpdatedAssumptionLog } from '@/lib/utils/crt'
import { crtYearFromParams } from '@/lib/utils/crtPeriodParams'
import { queryClient } from '@/main'
import { UpdateBpcActuals } from '@repo/backend/crt'
import { MONTH_KEYS } from '@repo/constants'
import { createFileRoute } from '@tanstack/react-router'
import { TriangleAlertIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BpcActualsDetailsDataTable from './-components/BpcActualsDetails/BpcActualsDetailsDataTable'

export const Route = createFileRoute('/_auth/_modules/crt/bpc-actuals/$year')({
  component: BpcActualsYear,
  validateSearch: (search) => bpcActualsDetailsSearchSchema.parse(search),
  loader: async ({ params }) => {
    const year = crtYearFromParams(params)
    // The backend resolves a period to its calendar year, so any month of the year names the import.
    const bpcActuals =
      year !== null
        ? await queryClient.fetchQuery(currentBpcActualsQueryOptions(`${year}-01-01`))
        : null

    return {
      crumb: bpcActuals?.name ?? params.year,
      year,
      bpcActualsId: bpcActuals ? String(bpcActuals.id) : null,
    }
  },
})

function BpcActualsYear() {
  const { year } = Route.useParams()
  const { bpcActualsId } = Route.useLoaderData()

  if (!bpcActualsId) {
    return <CrtImportPeriodNotFound period={year} otherPeriodsTo="/crt/bpc-actuals" />
  }

  return <BpcActualsDetails key={bpcActualsId} bpcActualsId={bpcActualsId} />
}

function BpcActualsDetails({ bpcActualsId }: { bpcActualsId: string }) {
  const { t } = useTranslation(['crt', 'common'])
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  const { data, isPending, isError } = useBpcActualsById(bpcActualsId, {
    ...search,
    page: search.page ?? 1,
    pageSize,
  })
  const { data: lastUpdatedBpcActualsTrail } = useLastUpdatedBpcActualsLog(bpcActualsId)

  const [formState, setFormState] = useState({
    name: '',
    description: '',
  })

  useEffect(() => {
    if (data) {
      setFormState({
        name: data.name,
        description: data.description ?? '',
      })
    }
  }, [data])

  const getUnsavedChanges = (): UpdateBpcActuals | null => {
    if (!data) return null

    const changes: UpdateBpcActuals = {}
    let hasChanges = false

    if (formState.name !== data.name) {
      changes.name = formState.name
      hasChanges = true
    }

    if (formState.description !== (data.description ?? '')) {
      changes.description = formState.description
      hasChanges = true
    }

    return hasChanges ? changes : null
  }

  const hasUnsavedChanges = getUnsavedChanges() !== null

  const lastUpdatedLog = formatLastUpdatedAssumptionLog(lastUpdatedBpcActualsTrail)

  const { mutate: updateBpcActuals, isPending: isUpdatePending } = useUpdateBpcActuals({
    bpcActualsId: Number(bpcActualsId),
  })

  const handleUpdateName = (name: string) => {
    setFormState((prev) => ({ ...prev, name }))
  }

  const handleUpdateDescription = (description: string) => {
    setFormState((prev) => ({ ...prev, description }))
  }

  const handleSave = () => {
    const unsavedChanges = getUnsavedChanges()
    if (!unsavedChanges) return

    updateBpcActuals(unsavedChanges)
  }

  if (isPending) {
    return (
      <PageContainer className="flex items-center justify-center">
        <Spinner size="lg" />
      </PageContainer>
    )
  }

  if (isError || !data) {
    return (
      <PageContainer>
        <Text>{t('errors.bpcActuals.fetchFailed')}</Text>
      </PageContainer>
    )
  }

  const unexpectedMonths = MONTH_KEYS.filter(
    (month) =>
      data.detailsMonthlyRowCounts[month].shouldBeZero &&
      data.detailsMonthlyRowCounts[month].count > 0,
  )

  return (
    <PageContainer maxWidth="wide" className="pb-28">
      <HStack gap="sm" className="items-start justify-between">
        <DetailsHeader
          name={formState.name}
          description={formState.description}
          onUpdateName={handleUpdateName}
          onUpdateDescription={handleUpdateDescription}
          permission="crt_bpc_actuals_edit"
        />

        <VStack gap="md" className="items-end">
          <Text className="text-muted-foreground text-sm">
            {t('year', { ns: 'common' })}: {data.year}
          </Text>

          <HStack gap="sm">
            <ExportCsvMenu
              items={[
                <ExportCsvMenuItem
                  label={t('export.viewData', { ns: 'common' })}
                  description={t('export.viewDataDescription', { ns: 'common' })}
                  getCsvExport={() =>
                    queryClient.ensureQueryData(
                      bpcActualsDetailsExportQueryOptions(bpcActualsId, search),
                    )
                  }
                  rowCount={data.detailsRowCount}
                />,
                <ExportCsvMenuItem
                  label={t('export.rawData', { ns: 'common' })}
                  description={t('export.rawDataDescription', { ns: 'common' })}
                  getCsvExport={() =>
                    queryClient.ensureQueryData(
                      bpcActualsDetailsRawExportQueryOptions(bpcActualsId),
                    )
                  }
                  rowCount={data.detailsRawRowCount}
                />,
              ]}
            />
            <CrtImportHistoryButtons
              otherPeriodsTo="/crt/bpc-actuals"
              uploadType="import-bpc-actuals-details"
              uploadAttributes={{ 'bpc-actuals-id': bpcActualsId }}
            />
          </HStack>
        </VStack>
      </HStack>

      {unexpectedMonths.length > 0 && (
        <Note
          tone="warning"
          icon={TriangleAlertIcon}
          note={
            <VStack gap="none">
              <Text weight="semibold">
                {t('bpcActualsDetails.notes.unexpectedMonthlyData.banner', {
                  count: unexpectedMonths.length,
                  months: unexpectedMonths
                    .map((month) => t(`monthKey.long.${month}`, { ns: 'common' }))
                    .join(', '),
                })}
              </Text>
              <Text>
                {t('bpcActualsDetails.notes.unexpectedMonthlyData.details', {
                  year: data.year,
                })}
              </Text>
            </VStack>
          }
        />
      )}

      <BpcActualsDetailsDataTable
        bpcActualsId={bpcActualsId}
        bpcActuals={data}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
      />

      <PermissionsGuard required={['crt_bpc_actuals_edit']}>
        <PendingChanges
          areChangesPending={hasUnsavedChanges}
          isSavePending={isUpdatePending}
          lastUpdatedLog={lastUpdatedLog}
          onSaveChanges={handleSave}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
